import { GridColDef } from '@mui/x-data-grid-premium';
import type { GridFilterModel } from '@mui/x-data-grid-premium';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tableSettingsProps, useTableSettings } from '../tableSettings/useTableSettings';
import tableHelper from 'src/utils/tableHelper';
import { useGetAllCustomers, useGetCustomersBySearch } from 'src/hooks/api/requestConfigs/orders';
import { canUseCustomerKeyValuePairs } from 'src/utils/enviromentValues';

// - delete this whole if + legacyCustomerColumns() when all servers are >= 2
const legacyCustomerColumns = (): GridColDef[] => [
  {
    field: 'actions',
    getActions: (params) =>
      tableHelper.buttonColumn(params, 'Edit', '/orders/customers/edit?id=', 'AccountId', 'Edit Customer'),
    type: 'actions',
    headerName: 'View',
    minWidth: 80,
    maxWidth: 120,
    flex: 0.6,
  },

  { field: 'AccountId', headerName: 'Account Id', minWidth: 130 },
  { field: 'CustomerNameDisplayName', headerName: 'Customer Name', flex: 1, minWidth: 220 },

  { field: 'CustomerTypeId', headerName: 'Customer Type', type: 'number', minWidth: 140 },
  { field: 'CustomerCategoryId', headerName: 'Customer Category', type: 'number', minWidth: 160 },

  { field: 'LegalAddressBookPostCode', headerName: 'Post Code', minWidth: 120 },
  { field: 'LegalAddressBookCity', headerName: 'City', minWidth: 160 },
  { field: 'LegalAddressBookCountryIso3166Name', headerName: 'Country', minWidth: 140 },

  { field: 'LegalAddressBookPhoneNumber', headerName: 'Phone', minWidth: 160 },
  { field: 'LegalAddressBookMobileNumber', headerName: 'Mobile', minWidth: 160 },
  { field: 'LegalAddressEmailAddress', headerName: 'Email', flex: 1, minWidth: 240 },

  {
    field: 'Disabled',
    headerName: 'Disabled',
    type: 'boolean',
    minWidth: 120,
    renderCell: (params) => (params.value ? 'Yes' : 'No'),
  },
  {
    field: 'Blocked',
    headerName: 'Blocked',
    type: 'boolean',
    minWidth: 120,
    renderCell: (params) => (params.value ? 'Yes' : 'No'),
  },

  { field: 'OrganisationId', headerName: 'Organisation', type: 'number', minWidth: 140 },
  { field: 'OrderDefaultCurrencyId', headerName: 'Currency', type: 'number', minWidth: 120 },
  { field: 'SalesPriceListId', headerName: 'Price List', type: 'number', minWidth: 140 },

  { field: 'CreateDate', headerName: 'Created', type: 'dateTime', valueGetter: tableHelper.getDateColumn, minWidth: 180 },
];

const SEARCHABLE_FILTER_FIELDS = new Set<string>([
  'CustomerNameDisplayName',
  'LegalAddressBookPhoneNumber',
  'LegalAddressBookMobileNumber',
  'LegalAddressEmailAddress',
]);

const normalizeSearch = (v: any) => String(v ?? '').trim();

const asRowArray = (v: any): any[] => {
  if (Array.isArray(v)) return v;
  if (Array.isArray(v?.rows)) return v.rows;
  if (Array.isArray(v?.data)) return v.data;
  if (Array.isArray(v?.data?.rows)) return v.data.rows;
  return [];
};

export const useCustomersTable = (): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const { rows: allRows, status } = useGetAllCustomers();
  const idPath = ['AccountId'];

  const [searchValue, setSearchValue] = useState<string>('');
  const searchedRows = useGetCustomersBySearch(searchValue);

  const rows = useMemo(() => {
    const base = asRowArray(allRows);
    const extra = asRowArray(searchedRows);

    const map = new Map<string | number, any>();
    for (const r of base) {
      const id = (r as any)?.AccountId;
      if (id !== null && id !== undefined) map.set(id, r);
    }
    for (const r of extra) {
      const id = (r as any)?.AccountId;
      if (id !== null && id !== undefined && !map.has(id)) map.set(id, r);
    }
    return Array.from(map.values());
  }, [allRows, searchedRows]);

  const columns: GridColDef[] = useMemo(() => {
    if (!canUseCustomerKeyValuePairs) return legacyCustomerColumns();

    return [
      {
        field: 'actions',
        getActions: (params) =>
          tableHelper.buttonColumn(params, 'Edit', '/orders/customers/edit?id=', 'AccountId', 'Edit Customer'),
        type: 'actions',
        headerName: 'View',
        minWidth: 80,
        maxWidth: 120,
        flex: 0.6,
      },

      { field: 'AccountId', headerName: 'Account Id', minWidth: 130 },
      { field: 'CustomerNameDisplayName', headerName: 'Customer Name', flex: 1, minWidth: 220 },

      { field: 'CustomerType_Value', headerName: 'Customer Type', minWidth: 140, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'CustomerCategory_Value', headerName: 'Customer Category', minWidth: 160, valueGetter: tableHelper.getFieldKeyOrValue },

      { field: 'LegalAddressBookPostCode', headerName: 'Post Code', minWidth: 120 },
      { field: 'LegalAddressBookCity', headerName: 'City', minWidth: 160 },
      { field: 'LegalAddressBookCountryIso3166Name', headerName: 'Country', minWidth: 140 },

      { field: 'LegalAddressBookPhoneNumber', headerName: 'Phone', minWidth: 160 },
      { field: 'LegalAddressBookMobileNumber', headerName: 'Mobile', minWidth: 160 },
      { field: 'LegalAddressEmailAddress', headerName: 'Email', flex: 1, minWidth: 240 },

      {
        field: 'Disabled',
        headerName: 'Disabled',
        type: 'boolean',
        minWidth: 120,
        renderCell: (params) => (params.value ? 'Yes' : 'No'),
      },
      {
        field: 'Blocked',
        headerName: 'Blocked',
        type: 'boolean',
        minWidth: 120,
        renderCell: (params) => (params.value ? 'Yes' : 'No'),
      },

      { field: 'Organisation_Value', headerName: 'Organisation', minWidth: 200, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'OrderDefaultCurrency_Value', headerName: 'Currency', minWidth: 120, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'SalesPriceList_Value', headerName: 'Price List', minWidth: 200, valueGetter: tableHelper.getFieldKeyOrValue },

      { field: 'CreateDate', headerName: 'Created', type: 'dateTime', valueGetter: tableHelper.getDateColumn, minWidth: 180 },
    ];
  }, []);

  const tableName = 'CustomersTable';
  const tableSettings = useTableSettings({ columns, idPath, rows, tableName, status });

  // ✅ IMPORTANT: called unconditionally, always same order
  useAttachCustomerSearchListener(tableSettings?.apiRef, setSearchValue);

  return [columns, tableSettings];
};

const MIN_CHARS = 3;

// First time we ever search (or first after clearing) we wait longer.
// After that, refinements feel quicker but still trailing-only.
const FIRST_DELAY_MS = 1400;
const NEXT_DELAY_MS = 700;

const useAttachCustomerSearchListener = (apiRef: any, setSearchValue: React.Dispatch<React.SetStateAction<string>>) => {
  const latestRef = useRef<string>('');
  const debounceRef = useRef<any>(null);
  const hasSearchedRef = useRef<boolean>(false);

  const commit = useCallback((next: string) => {
    setSearchValue(next);
    hasSearchedRef.current = Boolean(next);
  }, [setSearchValue]);

  const schedule = useCallback((nextRaw: string) => {
    const next = normalizeSearch(nextRaw);
    if (next === latestRef.current) return;

    latestRef.current = next;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Clear immediately when user clears the input.
    if (!next) {
      commit('');
      return;
    }

    // Don’t search at all for very short input.
    if (next.length < MIN_CHARS) return;

    const delay = hasSearchedRef.current ? NEXT_DELAY_MS : FIRST_DELAY_MS;

    debounceRef.current = setTimeout(() => {
      commit(next);
    }, delay);
  }, [commit]);

  useEffect(() => {
    const api = apiRef?.current;
    if (!api?.subscribeEvent) return;

    const unsubscribe = api.subscribeEvent('filterModelChange', (model: GridFilterModel) => {
      const quick = Array.isArray(model?.quickFilterValues) ? model.quickFilterValues : [];
      const quickJoined = normalizeSearch(quick.filter(Boolean).join(' '));

      const items = Array.isArray(model?.items) ? model.items : [];
      const fieldFilters = items
        .filter((it) => it?.field && SEARCHABLE_FILTER_FIELDS.has(it.field))
        .map((it) => normalizeSearch(it?.value))
        .filter(Boolean)
        .join(' ');

      const next = quickJoined || normalizeSearch(fieldFilters);
      schedule(next);
    });

    return () => {
      try { unsubscribe?.(); } catch {}
      if (debounceRef.current) {
        try { clearTimeout(debounceRef.current); } catch {}
      }
    };
  }, [apiRef, schedule]);
};